import { useNavigate } from 'react-router-dom';
import React from 'react';
import TableLoading from '../../../components/Layout/Loading/TableLoading';
import { UseQueryResult } from 'react-query';
import { match } from 'ts-pattern';
import IconLink from '../../../components/TextLink/IconLink';
import { DuplicateIcon, PencilIcon } from '@heroicons/react/outline';
import { StrapiResponseList } from '../../../interfaces/commons';
import { CourseResponse } from '../../../interfaces/courses';
import BadgeStatus from '../../../components/BadgeStatus';
import DeleteModal from '../../../components/Modals/DeleteModal';
import { formatCurrency } from '../../../libs/utils/formatters/currency';
import BadgeBoolean from '../../../components/BadgeBoolean';
import moment from 'moment';
interface CoursesTableProps {
  query: UseQueryResult<StrapiResponseList<CourseResponse>>;
  deleteCourse: (id: string | number) => void;
  duplicateCourse: (id: string | number) => void;
  duplicatingCourseId: number | null;
}
const CoursesTable: React.FC<CoursesTableProps> = ({
  query,
  deleteCourse,
  duplicateCourse,
  duplicatingCourseId,
}) => {
  const tableElements = [
    'Data',
    'Corso',
    'Online',
    'Sezione',
    'Minimo partecipanti',
    'Partecipanti iscritti',
    'Partecipanti in attesa',
    'Quote di Partecipazione',
    'Entrate Consuntivo',
    'Uscite Consuntivo',
    'Margine Consuntivo',
    'Margine preventivo',
    'Stato',
    '',
    '',
  ];

  const navigate = useNavigate();

  return (
    <div className='-my-2 overflow-x-auto'>
      <div className='inline-block min-w-full py-2 align-middle'>
        <div className='overflow-hidden ring-1 border ring-black ring-opacity-5 '>
          {match(query)
            .with({ status: 'idle' }, { status: 'loading' }, () => (
              <TableLoading tableElements={tableElements} />
            ))
            .with({ status: 'error' }, () => <div>Errore...</div>)
            .with({ status: 'success' }, (query) => {
              if (query.data) {
                return (
                  <table className='min-w-full divide-y divide-gray-300'>
                    <thead className='bg-gray-50 text-sm uppercase text-gray-500'>
                      <tr>
                        {tableElements.map((element, i) => (
                          <th
                            className='font-normal px-6 py-3 text-left tracking-wide'
                            key={i}
                          >
                            {element}
                          </th>
                        ))}
                        <th className='font-normal px-6 py-3 text-left tracking-wide' />
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200 bg-white'>
                      {query.data.data.map((course) => {
                        const isNewTemplateDraft =
                          !course.attributes.status &&
                          !course.attributes.title;

                        const coursePath = isNewTemplateDraft
                          ? `nuovo-corso/${course.id}/dati-principali`
                          : `dettaglio-corso/${course.id}/dati-principali`;

                        return (
                          <tr
                            key={course.id}
                            className='cursor-pointer hover:bg-neutral-100'
                            onClick={() => {
                              navigate(coursePath);
                            }}
                          >
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes.startDate
                                ? moment(course.attributes.startDate).format(
                                    'DD/MM/YYYY'
                                  )
                                : '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes.title}
                            </td>
                            <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                              <BadgeBoolean
                                active={
                                  course.attributes.isOnline ? true : false
                                }
                              />
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes.section?.data
                                ? course.attributes.section?.data.attributes
                                    .name
                                : '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes.minParticipants || 0}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes?.programParticipants
                                ?.partecipants || 0}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {course.attributes?.programParticipants
                                ?.pendingParticipants || 0}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {formatCurrency(
                                course.attributes?.participationUnit || 0
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {formatCurrency(
                                Number(
                                  course.attributes?.programBalance
                                    ?.consuntivoEntrate || 0
                                )
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {formatCurrency(
                                Number(
                                  course.attributes?.programBalance
                                    ?.consuntivoUscite || 0
                                )
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {formatCurrency(
                                Number(
                                  course.attributes?.programBalance
                                    ?.consuntivoUnitario || 0
                                )
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {formatCurrency(
                                Number(
                                  course.attributes?.programBalance
                                    ?.preventivoUnitario || 0
                                )
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {isNewTemplateDraft ? (
                                <span className='flex items-center justify-center px-5 py-1.5 rounded-full text-sm font-normal bg-sky-600 text-white'>
                                  Nuovo
                                </span>
                              ) : (
                                <BadgeStatus
                                  status={course.attributes.status}
                                />
                              )}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 '>
                              <IconLink
                                orangeTxt
                                href={coursePath}
                              >
                                <PencilIcon className='w-6 h-6' />
                              </IconLink>
                            </td>

                            <td
                              className='whitespace-nowrap px-6 py-4 '
                              onClick={(event) => event.stopPropagation()}
                            >
                              <button
                                type='button'
                                disabled={
                                  isNewTemplateDraft ||
                                  duplicatingCourseId === course.id
                                }
                                onClick={() => duplicateCourse(course.id)}
                                title='Duplica corso'
                                aria-label='Duplica corso'
                                className='text-primary hover:text-primary-dark disabled:text-gray-300 disabled:cursor-not-allowed'
                              >
                                <DuplicateIcon className='w-6 h-6' />
                              </button>
                            </td>

                            <td
                              className='whitespace-nowrap px-6 py-4 '
                              onClick={(event) => event.stopPropagation()}
                            >
                              <DeleteModal
                                title='Eliminazione corso:'
                                buttonType='icon'
                                buttonTxt='Elimina corso'
                                buttonDisabled={
                                  course.attributes.status === 'published'
                                }
                                onConfirm={() => deleteCourse(course.id)}
                                description='Sei sicuro di voler eliminare il corso:'
                                item={course.attributes.title}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              } else {
                return (
                  <div className='text-gray-500 my-20 text-center'>
                    Non hai i permessi
                  </div>
                );
              }
            })
            .exhaustive()}
        </div>
      </div>
    </div>
  );
};

export default CoursesTable;
